import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import format from 'date-fns/format';

import Divider from 'components/common/Divider';
import { FORMAT_DATE } from 'constants/time';
import { LOADING } from 'constants/fetch';
import Loading from 'components/Loading';
import ProfileCardLayout from '../ProfileCardLayout';

import styles from './styles.module.scss';

const Profile = memo(() => {
  const { t } = useTranslation();
  const companyData = useSelector((state) => state.company.companyData);
  const statusFetchCompanyData = useSelector((state) => state.company.status);

  if (statusFetchCompanyData === LOADING) {
    return <Loading />;
  }

  return (
    <ProfileCardLayout>
      {/* Info Sections */}
      <div className={styles.infoSections}>
        {/* Basic Data */}
        <div className={styles.infoSection}>
          <h3 className={styles.infoSectionTitle}>{t('profile.profile_section.main_data')}</h3>
          <div className={styles.infoFieldsGrid}>
            <div className={styles.infoField}>
              <p className={styles.fieldTitle}>{t('profile.legal_address')}</p>
              <p className={styles.fieldValue}>{companyData.legal_address || '-'}</p>
            </div>
            <div className={styles.infoField}>
              <p className={styles.fieldTitle}>{t('profile.post_address')}</p>
              <p className={styles.fieldValue}>{companyData.post_address || '-'}</p>
            </div>
            <div className={styles.infoField}>
              <p className={styles.fieldTitle}>{t('profile.profile_section.more_subtopics.entity')}</p>
              <p className={styles.fieldValue}>{companyData.legal_form || '-'}</p>
            </div>
            <div className={styles.infoField}>
              <p className={styles.fieldTitle}>{t('profile.profile_section.more_subtopics.employee_number')}</p>
              <p className={styles.fieldValue}>{companyData.employees_count || '-'}</p>
            </div>
          </div>
        </div>

        <Divider />

        {/* Contact Data */}
        <div className={styles.infoSection}>
          <h3 className={styles.infoSectionTitle}>{t('profile.profile_section.contact')}:</h3>
          <div className={styles.infoFieldsGrid}>
            <div className={styles.infoField}>
              <p className={styles.fieldTitle}>{t('profile.edit_profile_section.phone')}</p>
              <p className={styles.fieldValue}>
                {companyData.phone ? (
                  <a
                    href={`tel:${companyData.phone.replace(/\D/g, '').startsWith('+') ? companyData.phone.replace(/\D/g, '') : `+${companyData.phone.replace(/\D/g, '')}`}`}
                  >
                    {companyData.phone.startsWith('+') ? companyData.phone : `+${companyData.phone.replace(/\D/g, '')}`}
                  </a>
                ) : (
                  '-'
                )}
              </p>
            </div>
            <div className={styles.infoField}>
              <p className={styles.fieldTitle}>{t('profile.profile_section.email')}</p>
              <p className={styles.fieldValue}>
                {companyData.email ? (
                  <a href={`mailto:${companyData.email}`}>{companyData.email}</a>
                ) : (
                  '-'
                )}
              </p>
            </div>
            <div className={styles.infoField}>
              <p className={styles.fieldTitle}>{t('profile.site')}</p>
              <p className={styles.fieldValue}>
                {companyData.site ? (
                  <a href={companyData.site.startsWith('http') ? companyData.site : `https://${companyData.site}`} target="_blank" rel="noopener noreferrer">
                    {companyData.site}
                  </a>
                ) : (
                  '-'
                )}
              </p>
            </div>
          </div>
        </div>

        <Divider />

        {/* Registration Data */}
        <div className={styles.infoSection}>
          <h3 className={styles.infoSectionTitle}>{t('profile.profile_section.registration')}:</h3>
          <div className={styles.infoFieldsGrid}>
            <div className={styles.infoField}>
              <p className={styles.fieldTitle}>{t('profile.profile_section.founding')}</p>
              <p className={styles.fieldValue}>
                {companyData.creation_date ? format(new Date(companyData.creation_date), FORMAT_DATE) : '-'}
              </p>
            </div>
            <div className={styles.infoField}>
              <p className={styles.fieldTitle}>{t('profile.profile_section.registration_platform_date')}</p>
              <p className={styles.fieldValue}>
                {companyData.created_at ? format(new Date(companyData.created_at), FORMAT_DATE) : '-'}
              </p>
            </div>
            <div className={styles.infoField}>
              <p className={styles.fieldTitle}>{t('profile.profile_section.tin')}</p>
              <p className={styles.fieldValue}>{companyData.payer_number || '-'}</p>
            </div>
          </div>
        </div>
      </div>
    </ProfileCardLayout>
  );
});

Profile.displayName = 'Profile';

export default Profile;
